import { defineStore } from 'pinia'
import {
  getCategories,
  getDefaultCategories,
  getProductTypes,
  searchPrintStore,
  getSharedItems
} from '@/features/printstore/PrintStoreApi'

import isEqual from 'lodash-es/isEqual'
import { logger } from '@/logger'

export const usePrintStoreStore = defineStore('printStore', {
  state: () => ({
    id: null,
    defaultPrintStoreId: 5,
    hasShared: false,
    categories: [],
    defaultCategories: [],
    productTypes: [],
    collectionFilter: { id: '0', name: 'Alles' },
    upload: {
      files: [],
      progress: 0
    },
    currentCollectionName: undefined,
    currentProductName: undefined,
    currentPackageName: undefined,
    orderDetails: {
      cart: [],
      productType: 'PrintStore'
    },
    searchResults: [],
    searchOptions: {
      searchString: '',
      productTypeIds: [0]
    },
    routeBeforeCheckout: undefined,
    searchInProgress: false
  }),
  persist: {
    storage: sessionStorage,
    pick: ['orderDetails']
  },
  getters: {
    getCategory: (state) => (categoryId) => {
      return (
        state.categories.find((category) => category.id === categoryId) ||
        state.defaultCategories.find((category) => category.id === categoryId)
      )
    },
    getSearchOptionsProductTypeIds: (state) => {
      return state.searchOptions.productTypeIds
    }
  },
  actions: {
    // mutations as actions
    fileProgress(file) {
      const item = this.upload.files.find((item) => item.id === file.id)
      if (item) item.progress = file.progress
    },
    uploadProgress(progress) {
      this.upload.progress = Math.floor(progress * 99)
    },
    addFile(file) {
      this.upload.files.push(file)
    },
    complete() {
      this.upload.files = []
      this.upload.progress = 100
    },
    setProductTypes(productTypes) {
      this.productTypes = productTypes
    },
    setCategories(categories) {
      this.categories = categories
    },
    setDefaultCategories(categories) {
      this.defaultCategories = categories
    },
    setShared(items) {
      this.hasShared = items.length > 0
    },
    setCurrentCollectionName(name) {
      this.currentCollectionName = name
    },
    setCurrentProductName(name) {
      this.currentProductName = name
    },
    setCurrentPackageName(name) {
      this.currentPackageName = name
    },
    updateOrderDetails(details) {
      this.orderDetails[details[0]] = details[1]
    },
    addToCart(product) {
      let added = false
      if (!this.orderDetails.cart.length) {
        this.orderDetails.cart.push(product)
      } else {
        for (let i = 0; i < this.orderDetails.cart.length; i++) {
          if (
            product.maxOrderAmount &&
            product.quantity >= product.maxOrderAmount &&
            product.id === this.orderDetails.cart[i].id
          ) {
            added = true
          } else if (
            product.type === 'package' &&
            product.id === this.orderDetails.cart[i].id
          ) {
            product.quantity =
              product.quantity + this.orderDetails.cart[i].quantity
            this.orderDetails.cart.splice(i, 1, product)
            added = true
            break
          } else if (
            product.type !== 'package' &&
            !product.isCustomizable &&
            this.orderDetails.cart[i].name === product.name &&
            isEqual(
              this.orderDetails.cart[i].printSpecification,
              product.printSpecification
            )
          ) {
            product.quantity =
              product.quantity + this.orderDetails.cart[i].quantity
            this.orderDetails.cart.splice(i, 1, product)
            added = true
            break
          } else if (
            product.type !== 'package' &&
            product.isCustomizable &&
            this.orderDetails.cart[i].name === product.name &&
            isEqual(
              this.orderDetails.cart[i].printSpecification,
              product.printSpecification
            ) &&
            isEqual(
              this.orderDetails.cart[i].customFields,
              product.customFields
            )
          ) {
            product.quantity =
              product.quantity + this.orderDetails.cart[i].quantity
            this.orderDetails.cart.splice(i, 1, product)
            added = true
          }
        }
        if (!added) {
          this.orderDetails.cart.push(product)
        }
      }
    },
    emptyCart() {
      this.orderDetails.cart = []
    },
    removeFromCart(product) {
      for (let i = 0; i < this.orderDetails.cart.length; i++) {
        if (isEqual(this.orderDetails.cart[i], product)) {
          this.orderDetails.cart.splice(i, 1)
        }
      }
    },
    updateCart(products) {
      this.orderDetails.cart = products
    },
    setSearchInProgress(value = true) {
      this.searchInProgress = value
    },
    setSearchString(value) {
      this.searchOptions.searchString = value
    },
    setSearchProductTypeIds(value) {
      this.searchOptions.productTypeIds = value
    },
    setSearchResults(value) {
      this.searchResults = value
    },
    setRouteBeforeCheckout(value) {
      this.routeBeforeCheckout = value
    },
    setSearchOptions(searchOptions) {
      this.searchOptions = searchOptions
    },
    removeCategory(id) {
      this.categories = this.categories.filter((category) => category.id !== id)
    },
    removeFromSearchResults(id) {
      this.searchResults = this.searchResults.filter(
        (item) => item.product.id !== id
      )
    },
    setCollectionFilter(collection) {
      this.collectionFilter = collection
    },
    // original actions
    async hydrate() {
      getProductTypes().then((productTypes) => {
        this.setProductTypes(productTypes)
      })
      getCategories().then((categories) => {
        this.setCategories(categories)
      })
      getDefaultCategories().then((defaultCategories) => {
        this.setDefaultCategories(defaultCategories)
      })
      getSharedItems().then((items) => {
        this.setShared(items)
      })
    },
    search(data) {
      this.setSearchInProgress(true)
      this.setSearchResults([])
      searchPrintStore(this.searchOptions, data.d)
        .then((results) => {
          this.setSearchResults(results)
          this.setSearchInProgress(false)
        })
        .catch((error) => {
          logger.error(error)
          this.setSearchResults([])
          this.setSearchInProgress(false)
        })
    }
  }
})
